import { NONE_SELECTED } from '@Components/ColumnFilterPopover';
import { CampaignLabelingResolution, CampaignLabelingRow } from '../data-qa-types';

// Human-readable labels for CampaignLabelingRow.resolution -- used both as the filter
// popover's option text and as the table's Resolution column badge.
export const RESOLUTION_LABEL: Record<CampaignLabelingRow['resolution'], string> = {
  [CampaignLabelingResolution.LABEL]: 'Needs Labeling',
  [CampaignLabelingResolution.FEATURIZE]: 'Needs Featurization'
};

// Categorical columns a SourceGroupTable lets the reviewer narrow by, via
// ColumnFilterPopover -- Campaign is filtered separately by free-text search instead (it's
// effectively unique per row, so a checkbox pick-list wouldn't help). Missing returns more
// than one value per row (a campaign can be missing both Tactic and Brand vs Nonbrand), same
// shape as Product Roadmap's multi-value Function facet. firstSeen/lastSeen are exact ISO
// dates -- genuinely enumerable (campaigns cluster on shared start/end dates) rather than
// free text, same reasoning Sean gave for wanting this over the text-search treatment
// (2026-09-01).
export type FilterableColumn =
  'channel' | 'platform' | 'missing' | 'resolution' | 'firstSeen' | 'lastSeen';

export const FILTERABLE_COLUMNS: FilterableColumn[] = [
  'channel',
  'platform',
  'missing',
  'resolution',
  'firstSeen',
  'lastSeen'
];

export type FacetSelection = Record<FilterableColumn, ReadonlySet<string>>;

export const emptyFacetSelection = (): FacetSelection => ({
  channel: new Set(),
  platform: new Set(),
  missing: new Set(),
  resolution: new Set(),
  firstSeen: new Set(),
  lastSeen: new Set()
});

const facetValues = (row: CampaignLabelingRow, column: FilterableColumn): string[] => {
  switch (column) {
    case 'missing':
      return row.missing;
    case 'channel':
      return [row.channel];
    case 'platform':
      return [row.platform];
    case 'firstSeen':
      return [row.firstSeen];
    case 'lastSeen':
      return [row.lastSeen];
    case 'resolution':
    default:
      return [RESOLUTION_LABEL[row.resolution]];
  }
};

// A column with nothing selected passes every row (no filter applied yet). NONE_SELECTED
// means the opposite extreme -- explicitly excludes every row. Otherwise a column requires at
// least one of the row's values to be selected (a union match within the column), while
// columns are ANDed together -- same semantics as Product Roadmap's facetPasses.
const facetPasses = (
  row: CampaignLabelingRow,
  column: FilterableColumn,
  selected: ReadonlySet<string>
): boolean => {
  if (selected.has(NONE_SELECTED)) return false;
  if (selected.size === 0) return true;
  return facetValues(row, column).some(v => selected.has(v));
};

export const filterRowsByFacets = (
  rows: CampaignLabelingRow[],
  selection: FacetSelection
): CampaignLabelingRow[] =>
  rows.filter(row =>
    FILTERABLE_COLUMNS.every(column => facetPasses(row, column, selection[column]))
  );

// Options for one column's popover, with counts against this group's full row set (not
// re-scoped by the other columns' current selections) -- Product Roadmap's chip counts
// stay live against everything else that's currently selected; this deliberately skips that
// refinement, since a group here is a much smaller, already-scoped list.
export const distinctColumnValues = (
  rows: CampaignLabelingRow[],
  column: FilterableColumn
): { value: string; count: number }[] => {
  const counts = new Map<string, number>();
  rows.forEach(row => {
    facetValues(row, column).forEach(v => counts.set(v, (counts.get(v) ?? 0) + 1));
  });
  return Array.from(counts.entries())
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => a.value.localeCompare(b.value));
};
